import { useState, useEffect } from 'react'
import { MoreHorizontal, PlusCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { useAuth } from '@/contexts/auth-context'
import { supabase } from '@/utils/supabase/client'
import { Supplier } from '@/types'
import { SupplierFormModal } from './supplier-form-modal'
import { ConfirmationDialog } from './confirmation-dialog'
// TODO: Create SupplierFormModal
// import { SupplierFormModal } from './supplier-form-modal'

export function SupplierList() {
  const [suppliers, setSuppliers] = useState<Supplier[]>([])
  const [loading, setLoading] = useState(true)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(null)
  const [isConfirmOpen, setIsConfirmOpen] = useState(false)
  const { currentOrganization } = useAuth()

  useEffect(() => {
    if (currentOrganization) {
      fetchSuppliers()
    }
  }, [currentOrganization])

  const fetchSuppliers = async () => {
    if (!currentOrganization) return
    setLoading(true)
    const { data } = await supabase
      .from('suppliers')
      .select('*')
      .eq('organization_id', currentOrganization.id)
    
    if (data) setSuppliers(data)
    setLoading(false)
  }

  const handleSave = () => {
    fetchSuppliers();
  }

  const handleEdit = (supplier: Supplier) => {
    setSelectedSupplier(supplier);
    setIsModalOpen(true);
  }

  const handleDelete = (supplier: Supplier) => {
    setSelectedSupplier(supplier);
    setIsConfirmOpen(true);
  }

  const confirmDelete = async () => {
    if (!selectedSupplier) return;
    await supabase.from('suppliers').delete().eq('id', selectedSupplier.id)
    fetchSuppliers();
    setIsConfirmOpen(false);
    setSelectedSupplier(null);
  }

  return (
    <>
      <div className="flex justify-end mb-4">
        <Button onClick={() => {
          setSelectedSupplier(null);
          setIsModalOpen(true);
        }}>
          <PlusCircle className="h-4 w-4 mr-2" />
          Add Supplier
        </Button>
      </div>
       {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Contact Person</TableHead>
              <TableHead>Phone</TableHead>
              <TableHead>Email</TableHead>
              <TableHead><span className="sr-only">Actions</span></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {suppliers.map(supplier => (
              <TableRow key={supplier.id}>
                <TableCell className="font-medium">{supplier.name}</TableCell>
                <TableCell>{supplier.contact_person ?? 'N/A'}</TableCell>
                <TableCell>{supplier.phone ?? 'N/A'}</TableCell>
                <TableCell>{supplier.email ?? 'N/A'}</TableCell>
                <TableCell>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button aria-haspopup="true" size="icon" variant="ghost">
                        <MoreHorizontal className="h-4 w-4" />
                        <span className="sr-only">Toggle menu</span>
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuLabel>Actions</DropdownMenuLabel>
                      <DropdownMenuItem onSelect={() => handleEdit(supplier)}>Edit</DropdownMenuItem>
                      <DropdownMenuItem onSelect={() => handleDelete(supplier)}>Delete</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      <SupplierFormModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSave}
        supplier={selectedSupplier}
      />
      <ConfirmationDialog
        isOpen={isConfirmOpen}
        onClose={() => setIsConfirmOpen(false)}
        onConfirm={confirmDelete}
        title="Are you sure?"
        description="This action cannot be undone. This will permanently delete the supplier."
      />
    </>
  )
} 